import { ReactNode, SVGProps } from 'react';

// utils
import { cn } from 'utils/cn';

/**
 * Factory for the vendored glyphs in `./index.tsx`.
 *
 * The path data alone does not render an icon the way the app expects it: both
 * icon libraries the glyphs came from supplied a sizing and colouring contract
 * from their own stylesheet, and every call site was written against it. This
 * reproduces that contract so the sweep away from those packages is a change of
 * import path and nothing else.
 *
 * The Material and Ant Design glyphs are sized by `width/height: 1em`,
 * `display: inline-block`, `fill: currentColor`, `flex-shrink: 0`,
 * `user-select: none`, a 200ms fill transition and `font-size: 1.5rem`. The
 * font size is what `1em` resolves against, so a helper that sets the geometry
 * but not the size renders every icon at the inherited body size instead.
 *
 * The geometry is emitted as classes rather than an inline `style`: call sites
 * size icons with Tailwind utilities (`size-4`, `h-3.5!`), which an inline
 * `width`/`height` would silently beat. Passed through `cn()`, `tailwind-merge`
 * drops the base when the caller supplies its own and keeps it when they do not.
 */

/** The `fontSize` scale: 20, 24 and 35px at a 16px root. */
const FONT_SIZE = {
    /*
     * Deliberately empty: font-size inherits on its own, and an arbitrary
     * `text-[inherit]` reads as a text colour to tailwind-merge, not a size.
     */
    inherit: '',
    small: 'text-[1.25rem]',
    medium: 'text-[1.5rem]',
    large: 'text-[2.1875rem]',
} as const;

/* `inline-block` has to be spelled out: Preflight's `@layer base` makes a bare `svg` `display: block`. */
const BASE = 'inline-block h-[1em] w-[1em] shrink-0 select-none transition-[fill] duration-200 ease-in-out';

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'color' | 'fontSize'> & {
    fontSize?: keyof typeof FONT_SIZE;
    /** An escape hatch for a literal colour, rendered as the `color` presentation attribute. */
    htmlColor?: string;
    /** Renders a `<title>`, which names the icon and stops it being hidden from assistive tech. */
    titleAccess?: string;
};

export type SvgIconComponent = (props: IconProps) => ReactNode;

/**
 * Build a glyph component.
 *
 * `defaults` carries whatever the source library put on the element itself —
 * Ant Design inherits its font size instead of setting one and sits on the text
 * baseline, lucide strokes rather than fills. A call site overrides any of it
 * through the normal prop spread.
 */
export const svg = (viewBox: string, children: ReactNode, defaults: IconProps = {}): SvgIconComponent => {
    const { className: baseClassName, fontSize: baseFontSize = 'medium', ...baseProps } = defaults;

    return function Icon({ className, fontSize = baseFontSize, htmlColor, titleAccess, ...rest }: IconProps) {
        return (
            <svg
                viewBox={viewBox}
                focusable="false"
                aria-hidden={titleAccess ? undefined : true}
                role={titleAccess ? 'img' : undefined}
                color={htmlColor}
                fill="currentColor"
                {...baseProps}
                {...rest}
                className={cn(BASE, FONT_SIZE[fontSize], baseClassName, className)}
            >
                {children}
                {titleAccess ? <title>{titleAccess}</title> : null}
            </svg>
        );
    };
};

/**
 * Ant Design's glyphs sat in a `span.anticon`, which set neither a font size nor a
 * `flex-shrink` — `shrink` is what puts the latter back, since `BASE` pins `shrink-0`
 * and tailwind-merge resolves the two as one group.
 */
export const ANT: IconProps = { fontSize: 'inherit', className: 'shrink align-[-0.125em]' };

/**
 * lucide draws with a stroke on an unfilled path: 1.5px, the weight that sits beside regular text. The stroke does
 * not scale with the glyph (a 24px drawing shown at 16px would otherwise paint two thirds of it), so the width is in
 * rendered pixels at any size, and so is a call site's `strokeWidth`.
 */
export const LUCIDE: IconProps = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className: '[&_*]:[vector-effect:non-scaling-stroke]',
};
